import React, { useState, useEffect, useRef } from "react";
import CompanyHeader from "../../components/CompanyHeader";
import CompanySideMenu from "../../components/CompanySideMenu";
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import noimage from "../../../assets/img/noimage.jpg";
import { getNewsDetail, fetchRelated, setSuccess, setError } from "../../reducers/companyContentManage"
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useSelector, useDispatch } from "react-redux";
import { formatDate, formatTimeAgo, formatNewsTitle, getMarkdownExcerpt, parseTags } from "../../utils/format";
import ReturnButton from "../../components/ReturnButton";
import CustomIconButton from "../../components/CustomIconButton";
import CustomButton from "../../components/CustomButton";
import NotFoundPage from "../../pages/NotFoundPage";
import Copyright from "../../components/Copyright";
import MarkdownViewer from "../../components/MarkdownViewer";

const RelatedNewsItem = ({ item }: any) => {
    const navigate = useNavigate();

    return (
        <div className="items-start bg-white rounded-2xl group alu-section-box-shadow">
            <div className="relative rounded-t-2xl aspect-video">
                <img
                    onClick={() => navigate("/company/news/detail/" + item.id)}
                    src={item.img}
                    alt={item.title}
                    className="w-full h-full object-contain rounded-t-2xl group-hover:opacity-80 group-hover:cursor-pointer"
                />
            </div>
            <div className="p-4 bg-white rounded-b-2xl w-full">
                <h5 
                    onClick={() => navigate("/company/news/detail/" + item.id)}
                    className="text-base font-bold alu-text-base-2 mb-4 group-hover:underline cursor-pointer underline-offset-1 whitespace-pre-wrap line-clamp-1 break-words break-all">
                    {item.title}
                </h5>
                <p className="mb-2 text-sm leading-[1.3125rem] alu-text-base-2 whitespace-pre-wrap line-clamp-2 break-words break-all">
                    {getMarkdownExcerpt(item.description)}
                </p>
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-6 flex-1">
                        <p className="text-sm leading-[1.3125rem] alu-text-base-1">{item.news_type_display}</p>
                        <p className="text-sm leading-[1.3125rem] alu-text-base-1">{formatTimeAgo(item.public_date)}</p>
                    </div>
                    {item?.tags && <p className="text-sm leading-[1.3125rem] alu-text-base-1">
                        {parseTags(String(item?.tags)).map((tag: string, index: number) => (
                            <span key={index} className="mr-1">#{tag.trim()}{index < parseTags(String(item?.tags)).length - 1 && ','}</span>
                        ))}
                    </p>}
                </div>
            </div>
        </div>
    );
};

const NewsDetail = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
    const {
        loading,
        success,
        error,
        detailData,
        relatedData,
        actionName,
        actionStatus,
    } = useSelector((state: any) => state.companyContentManage);
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const [mark, setMark] = useState(false);
    const newsCardRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [totalHeight, setTotalHeight] = useState(0);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (id) {
            const data = {
                id: Number(id),
            }
            dispatch(getNewsDetail(data));
            dispatch(fetchRelated(data));
        }
    }, [id]);
    

    interface faceDetail {
        images: string;
        title: string;
        news_type_display: string;
        contents: string;
        created_at: string;
        company_name: string;
    }
    const details: faceDetail = {
        ...detailData,
        news_type_display: detailData.news_type_display?.name || null,
        tags: detailData?.tags ? parseTags(String(detailData.tags)) : "",
    }

    interface faceRelated {
        images: string;
        title: string;
        news_type_display: string;
        contents: string;
        created_at: string;
    }
    const relatedList: faceRelated[] = relatedData.map((item: any) => ({
        ...item,
        img: item?.images || noimage,
        title: item.title,
        description: item.contents,
        news_type_display: item.news_type_display?.name || null
    }));

    // get max-height to display other items with overflow-y
    useEffect(() => {
        newsCardRefs.current = newsCardRefs.current.slice(0, relatedList.length);
    }, [relatedList]);

    useEffect(() => {
        if (relatedList.length > 5) {
            let height = newsCardRefs.current.slice(0, 5).reduce((acc, ref) => {
                if (ref) {
                    return acc + ref.offsetHeight;
                }
                return acc;
            }, 0);
            height += 16 * 4; // + gap items
            setTotalHeight(height);
        }
    }, [relatedList]);
    
    //Access to this page is denied
    if(!details.id) {
        dispatch(setSuccess(null));
        dispatch(setError(null));
        if(actionName == "getDetailNews" && actionStatus == "rejected") return <NotFoundPage />;
        return null;
    }

    return (
        <>
        <CompanyHeader />
        <div className="mt-6 px-6 flex gap-6 relative">
            <CompanySideMenu />
            <div className="flex-1">
            <div className="mb-4 flex items-center gap-4">
                <ReturnButton onClick={() => navigate(-1)} />
                <span className="text-sm alu-text-base-1">/</span>
                <h3 className="text-xl leading-[1.875rem] alu-text-base-2 font-bold">
                ニュースのプレビュー
                </h3>
            </div>

            <div className="flex gap-6">
                <div className="p-6 rounded-2xl bg-white h-max w-full alu-section-box-shadow mb-6">
                    <h2 className="text-2xl leading-9 font-bold alu-text-base-2 mb-4 whitespace-pre-wrap break-words break-all">
                        {details.title}
                    </h2>
                    <div className="flex items-center justify-between mb-6">
                        <p className="text-sm leading-[1.3125rem] alu-text-base-1 flex gap-2">
                            {details.news_type_display}
                            {details?.tags && <p className="text-sm leading-[1.3125rem] alu-text-base-1">
                                {details?.tags.map((tag: string, index: number) => (
                                    <span key={index} className="mr-1">#{tag.trim()}{index < details?.tags.length - 1 && ','}</span>
                                ))}
                            </p>}
                        </p>
                        <p className="text-sm leading-[1.3125rem] alu-text-base-1">
                            {formatDate(details.public_date)}
                        </p>
                    </div>
                    {details.images && (
                        <div className="mb-4 aspect-video">
                            <img
                                className="w-full h-full object-contain rounded-t-[0.25rem]"
                                src={details.images}
                                alt="news"
                            />
                        </div>
                    )}

                    <p className="text-base leading-[1.8rem] alu-text-base-2 whitespace-pre-wrap break-words break-all">
                        <MarkdownViewer content={details.contents} />
                    </p>
                </div>
                <div className="shrink-0 w-[320px]">
                <CustomButton
                    disabled
                    customStyle="mb-4"
                    width="full"
                >
                    相談する
                </CustomButton>
                
                {relatedList?.length > 0 && (
                <>
                    <h5 className="mb-4 font-bold text-base alu-text-base-2">
                        関連記事
                    </h5>
                    <div
                        className={`custom-scrollbar flex flex-col gap-4 mb-4 ${relatedList.length > 5 ? 'overflow-y-auto' : ''}`}
                        style={{
                            maxHeight: relatedList.length > 5 ? `${totalHeight}px` : 'none',
                        }}
                    >
                        {relatedList.map((item, idx) => (
                        <div
                            key={idx}
                            ref={(el) => (newsCardRefs.current[idx] = el)}
                        >
                            <RelatedNewsItem key={idx} item={item} />
                        </div>
                        ))}
                    </div>
                </>
                )}
                </div>
            </div>
            </div>
        </div>
        <Copyright view="company" />
        </>
    );
};

export default NewsDetail;
